import { t } from "../lib/i18n";
import React, { useState, useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import { searchDocuments } from '../api';
import api from '../api';
import Tooltip from './Tooltip'; // Import the Tooltip component

const AIChatWindow: React.FC = () => {
  const [message, setMessage] = useState('');
  const [chatHistory, setChatHistory] = useState<{ user: string; bot: string }[]>([]);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const chatContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [chatHistory, isOpen]);

  const handleSendMessage = async () => {
    if (!message.trim()) return;

    const userMessage = message;
    setChatHistory(prev => [...prev, { user: userMessage, bot: '...' }]);
    setMessage('');
    setLoading(true);

    try {
      const botResponse = await searchDocuments(userMessage);
      setChatHistory(prev => {
        const newHistory = [...prev];
        newHistory[newHistory.length - 1].bot = botResponse;
        return newHistory;
      });
    } catch (error) {
      console.error('Error searching documents:', error);
      setChatHistory(prev => {
        const newHistory = [...prev];
        newHistory[newHistory.length - 1].bot = 'Sorry, I encountered an error. Please try again.';
        return newHistory;
      });
    } finally {
      setLoading(false);
    }
  };

  const handleViewDocument = async (documentId: string) => {
    try {
      const response = await api.get(`/documents/${documentId}/view`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: response.headers['content-type'] as string }));
      window.open(url, '_blank');
    } catch (error: any) {
      console.error('Error viewing document:', error.response?.data || error.message);
    }
  };

  return (
    <>
      {/* Floating Action Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="fixed bottom-6 left-6 w-16 h-16 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-full shadow-2xl flex items-center justify-center hover:shadow-[0_0_20px_rgba(79,70,229,0.5)] transform hover:scale-105 transition-all duration-300 z-50 focus:outline-none focus:ring-4 focus:ring-indigo-300"
        aria-label={t("aIChatWindow.k1")}
      >
        {isOpen ? (
          <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        ) : (
          <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
          </svg>
        )}
      </button>

      {/* Floating Chat Window */}
      <div
        className={`fixed bottom-28 left-6 w-96 max-w-[calc(100vw-3rem)] bg-white/95 backdrop-blur-md shadow-2xl rounded-2xl flex flex-col z-50 border border-gray-200 overflow-hidden transform transition-all duration-300 ease-in-out ${
          isOpen ? 'translate-y-0 opacity-100 visible' : 'translate-y-10 opacity-0 invisible'
        }`}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-4 flex justify-between items-center shadow-md">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-white/20 rounded-lg backdrop-blur-sm">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
            <div>
              <h3 className="font-bold text-lg leading-tight">{t("aIChatWindow.k2")}</h3>
              <p className="text-xs text-blue-100">{t("aIChatWindow.k3")}</p>
            </div>
          </div>
          <button onClick={() => setIsOpen(false)} className="text-white hover:text-indigo-200 focus:outline-none transition-colors">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Messages Area */}
        <div ref={chatContainerRef} className="h-96 overflow-y-auto p-5 bg-gray-50/50 flex flex-col space-y-4 scroll-smooth">
          {chatHistory.length === 0 && (
            <div className="text-center text-gray-400 mt-10">
              <svg className="w-16 h-16 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
              <p className="text-sm">{t("aIChatWindow.k4")}</p>
            </div>
          )}
          {chatHistory.map((chat, index) => (
            <div key={index} className="flex flex-col space-y-2">
              <div className="flex justify-end">
                <div className="bg-gradient-to-br from-blue-500 to-indigo-500 text-white rounded-2xl rounded-tr-sm p-3 max-w-[85%] shadow-md">
                  <p className="text-sm">{chat.user}</p>
                </div>
              </div>
              <div className="flex justify-start">
                <div className="bg-white text-gray-800 border border-gray-100 rounded-2xl rounded-tl-sm p-4 max-w-[90%] shadow-md prose prose-sm prose-blue">
                  <ReactMarkdown
                    components={{
                      a: ({node, ...props}) => {
                        if (props.href?.includes('/documents?documentId=')) {
                          const documentId = props.href.split('=').pop();
                          return (
                            <button
                              onClick={() => handleViewDocument(documentId!)}
                              className="text-indigo-600 font-semibold hover:text-indigo-800 hover:underline transition-colors"
                            >
                              {props.children}
                            </button>
                          );
                        }
                        return <a {...props} target="_blank" rel="noopener noreferrer" className="text-indigo-600 font-semibold hover:text-indigo-800 hover:underline transition-colors" />;
                      }
                    }}
                  >
                    {chat.bot}
                  </ReactMarkdown>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Input Area */}
        <div className="p-4 bg-white border-t border-gray-100">
          <div className="flex items-center bg-gray-50 border border-gray-200 rounded-xl overflow-hidden focus-within:ring-2 focus-within:ring-indigo-500 focus-within:border-transparent transition-all shadow-inner">
            <input
              type="text"
              className="flex-grow bg-transparent p-3 text-sm focus:outline-none text-gray-700 placeholder-gray-400"
              value={message}
              onChange={e => setMessage(e.target.value)}
              onKeyPress={e => e.key === 'Enter' && handleSendMessage()}
              placeholder={t("aIChatWindow.k5")}
              disabled={loading}
            />
            <button
              className="p-3 text-indigo-600 hover:text-indigo-800 disabled:text-gray-400 transition-colors focus:outline-none"
              onClick={handleSendMessage}
              disabled={loading || !message.trim()}
            >
              {loading ? (
                <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              ) : (
                <svg className="w-5 h-5 transform rotate-90" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                </svg>
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default AIChatWindow;
